import React, { useEffect } from 'react';
import {
    Droplet, Database, BarChart3, Bot, Brain,
    Shield, Droplets, Zap, Layers, Filter
} from 'lucide-react';

// Data lifecycle steps - using oil industry metaphor
const steps = [
    {
        id: "drilling",
        title: "Extraction",
        icon: <Droplet className="w-12 h-12 text-blue-500" />,
        description: "Tap into untapped data reserves across your enterprise and strike digital gold",
        color: "from-blue-600 to-blue-400",
        link: "/business-solutions",
        features: [
            { icon: <Droplet />, text: "Discover hidden data wells" },
            { icon: <Layers />, text: "Access previously unreachable sources" },
            { icon: <Filter />, text: "Collect and filter raw data crude" }
        ]
    },
    {
        id: "storing",
        title: "Refinement",
        icon: <Database className="w-12 h-12 text-indigo-500" />,
        description: "Transform raw data into high-grade, enterprise-ready information assets",
        color: "from-indigo-600 to-indigo-400",
        link: "/automation",
        features: [
            { icon: <Database />, text: "High-capacity data reservoirs" },
            { icon: <Shield />, text: "Secure storage with leak protection" },
            { icon: <Layers />, text: "Structured and unstructured refinement" }
        ]
    },
    {
        id: "using",
        title: "Consumption",
        icon: <Droplets className="w-12 h-12 text-amber-500" />,
        description: "Fuel your business with refined data that powers growth and innovation",
        color: "from-amber-600 to-amber-400",
        link: "/dashboards",
        features: [
            { icon: <BarChart3 />, text: "High-octane analytics engines" },
            { icon: <Bot />, text: "AI-powered business combustion" },
            { icon: <Brain />, text: "Machine learning acceleration" }
        ]
    },
    {
        id: "ai-fuel",
        title: "Fuel For AI",
        icon: <Zap className="w-12 h-12 text-emerald-500" />,
        description: "Supercharge AI models with high-performance, curated data fuel",
        color: "from-emerald-600 to-emerald-400",
        link: "/ai-solutions",
        features: [
            { icon: <Brain />, text: "Advanced AI model training" },
            { icon: <Zap />, text: "High-energy data acceleration" },
            { icon: <Bot />, text: "Intelligent data optimization" }
        ]
    }
];

const DataLifecycleSection = ({ activeStep, setActiveStep, onNavigate }) => {
    // Auto-advance the active step every 5 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveStep((prev) => (prev + 1) % 4);
        }, 5000);
        return () => clearInterval(interval);
    }, [setActiveStep]);

    return (
        <section id="data-lifecycle" className="relative z-10 py-24 px-4">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <div className="inline-block px-3 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm font-medium mb-4 border border-blue-700/30">
                        From Raw Data to Refined Intelligence
                    </div>
                    <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight max-w-4xl mx-auto">
                        The Complete <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Data Refinery</span> Platform
                    </h2>
                    <p className="text-xl text-slate-300 leading-relaxed mb-10 max-w-3xl mx-auto">
                        Drill into your organization's untapped data reserves and refine them into high-value business fuel.
                    </p>
                </div>

                {/* Lifecycle Visual - Pipeline Design */}
                <div className="relative max-w-6xl mx-auto">
                    {/* Pipeline connector */}
                    <div className="hidden md:block absolute top-1/2 left-0 right-0 h-6 bg-gradient-to-r from-blue-900/50 via-indigo-900/50 to-blue-900/50 transform -translate-y-1/2 rounded-full border-t border-b border-blue-500/30"></div>

                    {/* Lifecycle Steps */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10">
                        {steps.map((step, index) => (
                            <div
                                key={step.id}
                                className={`group relative bg-gradient-to-b from-slate-800/80 to-slate-900/80 rounded-xl p-6 border transition-all duration-300 
                                ${activeStep === index ? 'border-blue-500 shadow-lg shadow-blue-500/20' : 'border-slate-700/50'} 
                                hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/20`}
                                onClick={() => setActiveStep(index)}
                            >
                                {/* Connector to pipeline - vertical line */}
                                <div className="hidden md:block absolute left-1/2 -top-10 w-2 h-10 bg-gradient-to-b from-blue-500/20 to-blue-500/50 transform -translate-x-1/2"></div>

                                {/* Oil valve for active step */}
                                {activeStep === index && (
                                    <div className={`hidden md:block absolute left-1/2 -top-14 transform -translate-x-1/2 w-8 h-8 bg-gradient-to-r ${step.color} rounded-full border-4 border-slate-900 z-20 shadow-lg shadow-blue-500/50`}></div>
                                )}

                                {/* Step icon with animated background for current step */}
                                <div className={`w-20 h-20 mx-auto mb-6 relative rounded-full border-4 ${activeStep === index ? 'border-blue-500' : 'border-slate-700'}`}>
                                    <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${step.color} opacity-10 ${activeStep === index ? 'animate-pulse' : ''}`} style={{ animationDuration: '3s' }}></div>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        {step.icon}
                                    </div>
                                </div>

                                <h3 className="text-2xl font-bold text-center mb-4">{step.title}</h3>
                                <p className="text-slate-300 text-center mb-6">{step.description}</p>

                                {/* Feature list */}
                                <ul className="space-y-3 mt-4">
                                    {step.features.map((feature, featureIndex) => (
                                        <li key={featureIndex} className="flex items-start">
                                            <div className={`bg-blue-500/20 p-1.5 rounded-full mr-3 mt-0.5`}>
                                                <div className="w-4 h-4 text-blue-400">
                                                    {feature.icon}
                                                </div>
                                            </div>
                                            <span className="text-sm text-slate-300">{feature.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export { steps };
export default DataLifecycleSection;